// src/hooks/useWorkReminder.ts
// Tarkistaa onko aktiivinen työjakso käynnissä ja näyttää muistutuksen kun 7h45min täyttyy
import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../styles/theme';

const REMINDER_MINUTES = 7 * 60 + 45; // 465 minuuttia
const POLL_INTERVAL_MS = 60_000; // tarkista kerran minuutissa

interface ActiveSession {
  id: string;
  startedAt: string;
  status: string;
}

export function useWorkReminder(isAuthenticated: boolean) {
  const [elapsedMinutes, setElapsedMinutes] = useState<number | null>(null);
  const [activeSession, setActiveSession]   = useState<ActiveSession | null>(null);
  const [dismissed, setDismissed]           = useState(false);
  const lastSessionId = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;

    async function check() {
      try {
        const session = await apiFetch<ActiveSession | null>('/time-tracking/me/active');
        setActiveSession(session);

        if (session?.startedAt) {
          const elapsed = Math.floor((Date.now() - new Date(session.startedAt).getTime()) / 60000);
          setElapsedMinutes(elapsed);

          // Reset dismissed-tila jos sessio on vaihtunut
          if (lastSessionId.current !== session.id) {
            lastSessionId.current = session.id;
            setDismissed(false);
          }
        } else {
          setElapsedMinutes(null);
          lastSessionId.current = null;
        }
      } catch {
        // Ei tehdä mitään virheestä — ei häiritä käyttäjää
      }
    }

    check();
    const interval = setInterval(check, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const showReminder = !dismissed &&
    activeSession !== null &&
    elapsedMinutes !== null &&
    elapsedMinutes >= REMINDER_MINUTES;

  return {
    showReminder,
    elapsedMinutes,
    dismiss: () => setDismissed(true),
  };
}
